/**
 * Keyboard focus on the quality door (proposal 9).
 *
 * Every list item and case card on Triage, Cases and Done carries
 * `data-door-item`; the controls a shortcut presses carry
 * `data-shortcut="<key>"`; the workspace's "← <list>" link carries
 * `data-door-back`. These helpers find them, so the skip link, the
 * shortcut hook and the "where does focus go after Send / Save / Mark seen"
 * rules read one vocabulary instead of each page wiring refs.
 */

export const DOOR_ITEM_SELECTOR = '[data-door-item]';
export const DOOR_BACK_SELECTOR = '[data-door-back]';

/** A control a person could press right now: not disabled, not hidden. */
function usable(el: HTMLElement): boolean {
  if ((el as HTMLButtonElement).disabled) return false;
  if (el.getAttribute('aria-disabled') === 'true') return false;
  return !el.closest('[hidden], [aria-hidden="true"], [inert]');
}

/** The items on the current list, in reading order. */
export function doorItems(root: ParentNode = document): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(DOOR_ITEM_SELECTOR)).filter(usable);
}

/** The first usable element for the first selector that has one. */
export function firstUsable(selectors: readonly string[], root: ParentNode = document): HTMLElement | null {
  for (const selector of selectors) {
    const hit = Array.from(root.querySelectorAll<HTMLElement>(selector)).find(usable);
    if (hit) return hit;
  }
  return null;
}

/** Focus the first match of the first selector that has one; false when none. */
export function focusFirst(selectors: readonly string[], root: ParentNode = document): boolean {
  const el = firstUsable(selectors, root);
  if (!el) return false;
  el.focus();
  return document.activeElement === el;
}

/** The control a shortcut key presses on this page, if one is there. */
export function shortcutTarget(key: string, root: ParentNode = document): HTMLElement | null {
  return firstUsable([`[data-shortcut="${key}"]`], root);
}

/** True when focus sits on nothing the person chose (the body, or a control
 *  that has since been removed or disabled). */
function focusIsLost(): boolean {
  const active = document.activeElement as HTMLElement | null;
  return !active || active === document.body || !active.isConnected || !usable(active);
}

/**
 * After an action that removes the control focus was on (a decision that
 * closes the decision bar, a send that ends the route form): focus goes
 * back to the list (the workspace's back link, or the first item), never to
 * whatever stage button the page shows next. Deferred one tick so it runs
 * after React has rendered the change; a person who has already moved focus
 * somewhere on purpose keeps it.
 */
export function returnFocusToList(): void {
  window.setTimeout(() => {
    if (!focusIsLost()) return;
    focusFirst([DOOR_BACK_SELECTOR, DOOR_ITEM_SELECTOR]);
  }, 0);
}
